import { useEffect, useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid2 as Grid,
  Typography,
  useTheme,
  Skeleton,
  Avatar,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ApexChart from "react-apexcharts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import axios from "axios";

const Chart = styled(ApexChart)``;

// Stat Card Component
const StatCard = ({
  title,
  value,
  change,
  changeLabel,
  icon,
  color,
  loading,
}) => {
  const isPositive = change >= 0;

  return (
    <Card
      sx={{
        height: "100%",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          width: "100px",
          height: "100px",
          background: "rgba(255,255,255,0.1)",
          borderRadius: "50%",
          transform: "translate(30%, -30%)",
        },
      }}
      style={{
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        color: "white",
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              {title}
            </Typography>
            {loading ? (
              <Skeleton
                variant="text"
                width={120}
                height={40}
                sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
              />
            ) : (
              <Typography variant="h4" fontWeight="bold">
                {value}
              </Typography>
            )}
            {change !== undefined && !loading && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                {isPositive ? (
                  <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} />
                )}
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {isPositive ? "+" : ""}
                  {change}% {changeLabel}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// Monthly Trend Chart Component
const MonthlyTrendChart = ({ data, title, loading }) => {
  const theme = useTheme();

  const chartOptions = {
    chart: {
      background: "transparent",
      toolbar: { show: false },
    },
    colors: ["#667eea"],
    dataLabels: { enabled: false },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 100],
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 2,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    stroke: { curve: "smooth", width: 3 },
    theme: { mode: theme.palette.mode },
    xaxis: {
      categories: data?.labels || [],
      labels: {
        style: { colors: theme.palette.text.secondary },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => `$${Math.round(value).toLocaleString()}`,
        style: { colors: theme.palette.text.secondary },
      },
    },
    tooltip: {
      theme: theme.palette.mode,
      y: {
        formatter: (value) => `$${value.toFixed(2)}`,
      },
    },
    legend: {
      show: false,
    },
  };

  const chartSeries = [
    {
      name: title,
      data: data?.values || [],
    },
  ];

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={300}
          />
        )}
      </CardContent>
    </Card>
  );
};

// Multi-Series Trend Chart Component (for one-time payment types)
const MultiSeriesTrendChart = ({ data, title, loading }) => {
  const theme = useTheme();

  const chartOptions = {
    chart: {
      background: "transparent",
      toolbar: { show: false },
    },
    colors: ["#667eea", "#f093fb", "#11998e"],
    dataLabels: { enabled: false },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 100],
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 2,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    stroke: { curve: "smooth", width: 3 },
    theme: { mode: theme.palette.mode },
    xaxis: {
      categories: data?.labels || [],
      labels: {
        style: { colors: theme.palette.text.secondary },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => `$${Math.round(value).toLocaleString()}`,
        style: { colors: theme.palette.text.secondary },
      },
    },
    tooltip: {
      theme: theme.palette.mode,
      y: {
        formatter: (value) => `$${value.toFixed(2)}`,
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      labels: {
        colors: theme.palette.text.primary,
      },
    },
  };

  const chartSeries = [
    {
      name: "Notarization + Recording",
      data: data?.notaryRecord || [],
    },
    {
      name: "NOC Notarization",
      data: data?.nocNotary || [],
    },
    {
      name: "Signing Only",
      data: data?.signOnly || [],
    },
  ];

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={300}
          />
        )}
      </CardContent>
    </Card>
  );
};

// Pie Chart Component
const DistributionChart = ({ data, title, loading }) => {
  const theme = useTheme();

  const chartOptions = {
    chart: {
      background: "transparent",
    },
    colors: ["#667eea", "#764ba2", "#f093fb"],
    labels: data?.labels || [],
    legend: {
      position: "bottom",
      labels: {
        colors: theme.palette.text.primary,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `$${Math.round(val).toLocaleString()}`,
    },
    theme: { mode: theme.palette.mode },
    tooltip: {
      y: {
        formatter: (value) => `$${value.toFixed(2)}`,
      },
    },
  };

  const chartSeries = data?.values || [];

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton
            variant="circular"
            width={250}
            height={250}
            sx={{ mx: "auto" }}
          />
        ) : (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="donut"
            height={300}
          />
        )}
      </CardContent>
    </Card>
  );
};

const SalesRevenue = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    mrr: 0,
    recurringRevenue: {
      thisMonth: 0,
      lastMonth: 0,
      change: 0,
      byPlan: {},
      trend: { labels: [], values: [] },
    },
    oneTimeRevenue: {
      thisMonth: 0,
      lastMonth: 0,
      change: 0,
      byPlan: {},
      trend: { labels: [], values: [] },
    },
    totalRevenue: {
      thisMonth: 0,
      lastMonth: 0,
      change: 0,
      trend: { labels: [], values: [] },
    },
    planMRR: {},
    oneTimePaymentTypes: {
      labels: [],
      notaryRecord: [],
      nocNotary: [],
      signOnly: [],
    },
  });
  const [revenueByPlan, setRevenueByPlan] = useState({
    labels: [],
    values: [],
  });
  const smDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const fetchDataRef = useRef(null);

  useEffect(() => {
    const CACHE_KEY = "sales_revenue_stats";
    const CACHE_DURATION = 60 * 1000; // 1 minute in milliseconds

    // Helper function to set state from data
    const setStateFromData = (data) => {
      setStats({
        mrr: data.mrr || 0,
        recurringRevenue: data.recurringRevenue || {
          thisMonth: 0,
          lastMonth: 0,
          change: 0,
          byPlan: {},
          trend: { labels: [], values: [] },
        },
        oneTimeRevenue: data.oneTimeRevenue || {
          thisMonth: 0,
          lastMonth: 0,
          change: 0,
          byPlan: {},
          trend: { labels: [], values: [] },
        },
        totalRevenue: data.totalRevenue || {
          thisMonth: 0,
          lastMonth: 0,
          change: 0,
          trend: { labels: [], values: [] },
        },
        planMRR: data.planMRR || {},
        oneTimePaymentTypes: data.oneTimePaymentTypes || {
          labels: [],
          notaryRecord: [],
          nocNotary: [],
          signOnly: [],
        },
      });
      setRevenueByPlan(data.revenueByPlan || { labels: [], values: [] });
    };

    // Check cache first
    const checkCache = () => {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const now = Date.now();
          const age = now - timestamp;

          // If cache is less than 1 minute old, use it
          if (age < CACHE_DURATION) {
            setStateFromData(data);
            setLoading(false);
            return true; // Cache was used
          } else {
            // Cache expired, remove it
            localStorage.removeItem(CACHE_KEY);
          }
        }
      } catch (error) {
        console.error("Error reading cache:", error);
        localStorage.removeItem(CACHE_KEY);
      }
      return false; // Cache not used
    };

    // Fetch fresh data from API
    const fetchRevenueData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/nocApi/sales/revenue-stats`,
          { withCredentials: true }
        );

        if (res.data.success) {
          const data = res.data.data;

          // Update state
          setStateFromData(data);

          // Cache the data with timestamp
          try {
            localStorage.setItem(
              CACHE_KEY,
              JSON.stringify({
                data: data,
                timestamp: Date.now(),
              })
            );
          } catch (error) {
            console.error("Error caching revenue data:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching revenue data:", error);
        // Set mock data for development
        setStats({
          mrr: 12500,
          recurringRevenue: {
            thisMonth: 12500,
            lastMonth: 12000,
            change: 4,
            byPlan: { Premium: 8500, Rockstar: 4000 },
            trend: {
              labels: [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ],
              values: [
                12000, 12500, 13000, 12800, 13500, 14000, 13800, 14500, 14200,
                15000, 14800, 15200,
              ],
            },
          },
          oneTimeRevenue: {
            thisMonth: 2700,
            lastMonth: 1800,
            change: 50,
            byPlan: { Premium: 1500, Rockstar: 1200 },
            trend: {
              labels: [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ],
              values: [
                2000, 1800, 2200, 1900, 2500, 2400, 2100, 2300, 2600, 2800,
                2700, 2700,
              ],
            },
          },
          totalRevenue: {
            thisMonth: 15200,
            lastMonth: 13800,
            change: 10,
            trend: {
              labels: [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ],
              values: [
                14000, 14300, 15200, 14700, 16000, 16400, 15900, 16800, 16800,
                17800, 17500, 17900,
              ],
            },
          },
          planMRR: {
            Free: 0,
            Premium: 8500,
            Rockstar: 4000,
          },
          oneTimePaymentTypes: {
            labels: [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ],
            notaryRecord: [
              1200, 1000, 1300, 1100, 1500, 1400, 1200, 1300, 1600, 1800, 1700,
              1700,
            ],
            nocNotary: [
              600, 500, 700, 600, 800, 750, 650, 700, 800, 900, 850, 850,
            ],
            signOnly: [
              200, 300, 200, 200, 200, 250, 250, 300, 200, 100, 150, 150,
            ],
          },
        });
        setRevenueByPlan({
          labels: ["Premium", "Rockstar"],
          values: [8500, 4000],
        });
      } finally {
        setLoading(false);
      }
    };

    // Check cache first, if not available or expired, fetch fresh data
    const cacheUsed = checkCache();
    if (!cacheUsed && !fetchDataRef.current) {
      fetchDataRef.current = true;
      fetchRevenueData();
    }
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Box sx={{ p: smDown ? 1 : 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom color="primary">
          Revenue Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Financial overview for Free, Premium, and Rockstar plans
        </Typography>
      </Box>

      {/* Top Stat Cards */}
      <Grid container spacing={smDown ? 1 : 2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Monthly Recurring Revenue"
            value={formatCurrency(stats.mrr)}
            icon={<AccountBalanceIcon />}
            color="#667eea"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Recurring Revenue This Month"
            value={formatCurrency(stats.recurringRevenue.thisMonth)}
            change={stats.recurringRevenue.change}
            changeLabel="vs last month"
            icon={<AttachMoneyIcon />}
            color="#11998e"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="One-Time Revenue This Month"
            value={formatCurrency(stats.oneTimeRevenue.thisMonth)}
            change={stats.oneTimeRevenue.change}
            changeLabel="vs last month"
            icon={<AttachMoneyIcon />}
            color="#f093fb"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Total Revenue This Month"
            value={formatCurrency(stats.totalRevenue.thisMonth)}
            change={stats.totalRevenue.change}
            changeLabel="vs last month"
            icon={<AttachMoneyIcon />}
            color="#f5576c"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <MonthlyTrendChart
            data={stats.recurringRevenue.trend}
            title="Recurring Revenue Trend"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <MonthlyTrendChart
            data={stats.oneTimeRevenue.trend}
            title="One-Time Revenue Trend"
            loading={loading}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <MultiSeriesTrendChart
            data={stats.oneTimePaymentTypes}
            title="One-Time Payment Types Trend"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <DistributionChart
            data={revenueByPlan}
            title="MRR by Plan"
            loading={loading}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <MonthlyTrendChart
            data={stats.totalRevenue.trend}
            title="Total Revenue Trend"
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesRevenue;
